import { Injectable, signal } from '@angular/core';

import CS_default_sprite from '@connect-services/client/assets/sprites/CS_default_sprite.json';
import CS_star_eau_sprite from '@connect-services/client/assets/sprites/CS_star-eau_sprite.json';
import { AssertService } from '@connect-services/client/services/assert.service';
import { MapboxStyle } from '@connect-services/src/sdk/api';

export const DEFAULT_RGB_COLOR = 'rgb(0,0,0)';
export const DEFAULT_ICON_HEIGHT_WIDTH = 24;

export interface LegendInformationAndStyle {
    color?: string[];
    group?: string;
    iconOpacity?: number;
    legendStyle: LegendStyle;
    lineColor?: string;
    name?: string;
    rotation?: number[];
    sizeRatio?: number[];
    spriteInformationList: SpriteInformationWithUrl[];
}

export interface SpriteInformation {
    height: number;
    pixelRatio: number;
    sdf: boolean;
    width: number;
    x: number;
    y: number;
}

export interface SpriteInformationWithUrl extends SpriteInformation {
    url: string;
}

export interface LegendStyle {
    description: string | null;
    style: Record<string, unknown>;
    type: MapboxStyle.TypeEnum;
}

@Injectable({
    providedIn: 'root',
})
export class LegendService {
    private static readonly DEFAULT_CIRCLE_RADIUS = 5;
    private static readonly DEFAULT_COLOR = 'rgb(0 ,0, 0)';
    private static readonly SPRITES_DATA: Record<string, SpriteInformationWithUrl> = {
        ...LegendService.prepareSpriteInformation(CS_default_sprite, '/assets/sprites/CS_default_sprite.png'),
        ...LegendService.prepareSpriteInformation(
            CS_star_eau_sprite,
            '/assets/sprites/CS_star-eau_sprite.png',
            'star-eau',
        ),
    };

    public readonly isLegendOpen = signal(false);

    protected readonly StyleEnum = MapboxStyle.TypeEnum;

    private static prepareSpriteInformation(
        spriteInformation: Record<string, SpriteInformation>,
        url: string,
        prefix?: string,
    ): Record<string, SpriteInformationWithUrl> {
        return Object.fromEntries(
            Object.entries(spriteInformation).map(
                ([key, value]: [string, SpriteInformation]): [string, SpriteInformationWithUrl] => [
                    prefix ? `${prefix}:${key}` : key,
                    {
                        ...value,
                        url: url,
                    },
                ],
            ),
        );
    }

    public getLegendStyles(styles: MapboxStyle[]): LegendInformationAndStyle[] {
        return styles
            .filter((style: MapboxStyle) =>
                [this.StyleEnum.CIRCLE, this.StyleEnum.LINE, this.StyleEnum.FILL, this.StyleEnum.SYMBOL].includes(
                    style.type,
                ),
            )
            .map((style: MapboxStyle) => this.createLegendStyle(style));
    }

    private buildCircleStyle(source: MapboxStyle): {
        color?: string;
        name?: string;
        spriteInformation?: SpriteInformationWithUrl;
        style: LegendStyle['style'];
    } {
        const circleSpec = source;

        AssertService.isCircleLayerSpecification(circleSpec);

        const rawRadius = circleSpec.paint!['circle-radius'];
        // default is 5 https://maplibre.org/maplibre-style-spec/layers/#circle-radius
        const radius = typeof rawRadius === 'number' ? rawRadius : LegendService.DEFAULT_CIRCLE_RADIUS;

        const color = circleSpec.paint!['circle-color'] ?? LegendService.DEFAULT_COLOR;
        const strokeWidth = circleSpec.paint!['circle-stroke-width'] ?? 0;
        const strokeColor = circleSpec.paint!['circle-stroke-color'] ?? LegendService.DEFAULT_COLOR;

        const style: LegendStyle['style'] = {
            'background-color': color,
            'border-radius': '50%',
            height: `${radius * 2}px`,
            width: `${radius * 2}px`,
        };

        if (strokeWidth) {
            style['border'] = `${strokeWidth}px solid ${strokeColor}`;
        }

        return {
            style,
        };
    }

    private buildFillStyle(source: MapboxStyle): {
        color?: string;
        name?: string;
        spriteInformation?: SpriteInformationWithUrl;
        style: LegendStyle['style'];
    } {
        const fillSpec = source;

        AssertService.isFillLayerSpecification(fillSpec);

        const outlineColor = fillSpec.paint?.['fill-outline-color'];

        const iconName = fillSpec.paint?.['fill-pattern'];

        const style: LegendStyle['style'] = {
            height: `${DEFAULT_ICON_HEIGHT_WIDTH * 2}px`,
            width: `${DEFAULT_ICON_HEIGHT_WIDTH * 3}px`,
        };

        const fillColor = fillSpec.paint?.['fill-color'] ?? undefined;

        if (fillColor) {
            AssertService.isString(fillColor);
            style['background-color'] = fillColor;
        }

        let iconData: SpriteInformationWithUrl | undefined = undefined;

        if (iconName) {
            AssertService.isString(iconName);

            iconData = LegendService.SPRITES_DATA[iconName];

            style['background-position'] = `-${iconData.x}px -${iconData.y}px`;
            style['background-repeat'] = 'no-repeat';
        }

        if (outlineColor) {
            style['border'] = `1px solid ${outlineColor}`;
        }

        return {
            color: DEFAULT_RGB_COLOR,
            name: iconName,
            spriteInformation: iconData,
            style,
        };
    }

    private buildLineStyle(source: MapboxStyle): {
        color?: string;
        lineColor?: string;
        name?: string;
        spriteInformation?: SpriteInformationWithUrl;
        style: LegendStyle['style'];
    } {
        const lineSpec = source;

        AssertService.isLineLayerSpecification(lineSpec);

        const lineColor = lineSpec.paint?.['line-color'] ?? undefined;
        const iconName = lineSpec.paint?.['line-pattern'];

        const lineOpacity = lineSpec.paint?.['line-opacity'] ?? 1;
        const lineWidth = this.getLineWidth(lineSpec.paint?.['line-width']);

        const style: LegendStyle['style'] = {
            height: `${lineWidth}px`,
            opacity: lineOpacity,
            width: `${DEFAULT_ICON_HEIGHT_WIDTH * 3}px`,
        };

        let iconData: SpriteInformationWithUrl | undefined = undefined;

        if (lineColor) {
            AssertService.isString(lineColor);
            style['background-color'] = lineColor ?? LegendService.DEFAULT_COLOR;
        }

        if (iconName) {
            AssertService.isString(iconName);

            iconData = LegendService.SPRITES_DATA[iconName];

            style['background-position'] = `-${iconData.x}px -${iconData.y}px`;
        }

        const dashArray = lineSpec.paint!['line-dasharray'];
        if (dashArray) {
            style['border-bottom'] = `${lineWidth}px dashed ${lineColor}`;
            style['background-color'] = 'transparent';
        }

        return {
            color: DEFAULT_RGB_COLOR,
            lineColor: lineColor,
            name: iconName,
            spriteInformation: iconData,
            style,
        };
    }

    private buildSymbolSpriteStyle(source: MapboxStyle): {
        color: string;
        name?: string;
        rotation?: number;
        sizeRatio?: number;
        spriteInformation?: SpriteInformationWithUrl;
        style: LegendStyle['style'];
    } {
        const symbolSpec = source;

        AssertService.isSymbolLayerSpecification(symbolSpec);

        const style: LegendStyle['style'] = {};

        const iconName = symbolSpec.layout?.['icon-image'] ?? undefined;

        let iconData: SpriteInformationWithUrl | undefined = undefined;

        if (iconName) {
            AssertService.isString(iconName);

            iconData = LegendService.SPRITES_DATA[iconName];

            style['background-position'] = `-${iconData.x}px -${iconData.y}px`;
            style['background-repeat'] = 'no-repeat';
        }

        const symbolColor = symbolSpec.paint?.['icon-color'] ?? LegendService.DEFAULT_COLOR;
        AssertService.isString(symbolColor);

        const rotation = symbolSpec.layout?.['icon-rotate'] ?? 0;

        if (rotation) {
            AssertService.isNumber(rotation);
        }

        let sizeRatio = symbolSpec.layout?.['icon-size'] ?? 1;

        if (Array.isArray(sizeRatio)) {
            sizeRatio = 1;
        }

        if (sizeRatio) {
            AssertService.isNumber(sizeRatio);
        }

        return {
            color: symbolColor,
            name: iconName,
            rotation: rotation,
            sizeRatio: sizeRatio,
            spriteInformation: iconData,
            style: style,
        };
    }

    private createLegendStyle(source: MapboxStyle): LegendInformationAndStyle {
        const description = source.metadata?.infoDescription || null;
        const group = source.metadata?.legendGroup;

        const legendParams: LegendInformationAndStyle = {
            color: [],
            group: group,
            legendStyle: { description, style: {}, type: source.type },
            name: '',
            spriteInformationList: [],
        };

        let spriteInformationStyle: {
            color?: string;
            lineColor?: string;
            name?: string;
            rotation?: number;
            sizeRatio?: number;
            spriteInformation?: SpriteInformationWithUrl;
            style: LegendStyle['style'];
        } = {
            style: {},
        };

        switch (source.type) {
            case this.StyleEnum.CIRCLE: {
                spriteInformationStyle = this.buildCircleStyle(source);
                break;
            }
            case this.StyleEnum.LINE: {
                spriteInformationStyle = this.buildLineStyle(source);
                break;
            }
            case this.StyleEnum.FILL: {
                spriteInformationStyle = this.buildFillStyle(source);
                break;
            }
            case this.StyleEnum.SYMBOL: {
                AssertService.isSymbolLayerSpecification(source);
                if (!source.layout?.['icon-image']) {
                    break;
                }

                spriteInformationStyle = this.buildSymbolSpriteStyle(source);
                break;
            }
        }

        legendParams.color = spriteInformationStyle.color ? [spriteInformationStyle.color] : [];
        legendParams.rotation = spriteInformationStyle.rotation ? [spriteInformationStyle.rotation] : [];
        legendParams.sizeRatio = spriteInformationStyle.sizeRatio ? [spriteInformationStyle.sizeRatio] : [];

        legendParams.name = spriteInformationStyle.name;
        legendParams.lineColor = spriteInformationStyle.lineColor;
        legendParams.legendStyle = { ...legendParams.legendStyle, style: spriteInformationStyle.style };

        if (spriteInformationStyle.spriteInformation) {
            legendParams.spriteInformationList.push(spriteInformationStyle.spriteInformation);
        }

        return legendParams;
    }

    private getLineWidth(lineWidth: unknown): number {
        if (typeof lineWidth === 'number') {
            return lineWidth;
        }

        if (Array.isArray(lineWidth) && lineWidth[0] === 'step') {
            const widths: number[] = [];

            for (let i = 2; i < lineWidth.length; i += 2) {
                const width = lineWidth[i];

                if (typeof width === 'number') {
                    widths.push(width);
                }
            }

            if (widths.length > 0) {
                const index = Math.floor(widths.length / 2);

                return widths[index];
            }
        }

        return 1;
    }
}
